import {type ReactNode} from 'react';
import {
  CBS_ACTIVE_PILL_CLASS,
  CBS_CURRENT_PILL_CLASS,
  CBS_INACTIVE_PILL_CLASS,
} from '@common-web/components/account/cbsAccountsPresentation';
import {cn} from '@common-web/lib/utils';
import {useIsInternal} from '@common-web/providers/portalProvider';

export type MemberStatusBadgeStatus = 'active' | 'inactive' | 'termed';
export type MemberStatusBadgePortal = 'internal' | 'client';
export type MemberStatusBadgeTone = 'solid' | 'context' | 'search' | 'product';
export type MemberStatusBadgeSize = 'default' | 'compact' | 'context' | 'overview' | 'search';

export const MEMBER_PILL_SIZE_CLASS = 'px-2 py-0.5 text-xs font-semibold';

const SIZE_CLASSES: Record<MemberStatusBadgeSize, string> = {
  compact: 'px-1.5 py-0.5 text-xs font-semibold',
  context: MEMBER_PILL_SIZE_CLASS,
  default: 'px-3 py-1 text-xs font-medium',
  overview: 'px-3 py-1 text-xs font-semibold',
  search: MEMBER_PILL_SIZE_CLASS,
};

const CLIENT_SOLID_CLASSES: Record<MemberStatusBadgeStatus, string> = {
  active: 'border-0 bg-emerald text-white',
  inactive: 'border-0 bg-brand-gray text-white',
  termed: 'border-0 bg-brand-gray text-white',
};

const INTERNAL_SOLID_CLASSES: Record<MemberStatusBadgeStatus, string> = {
  active: CBS_ACTIVE_PILL_CLASS,
  inactive: CBS_INACTIVE_PILL_CLASS,
  termed: CBS_INACTIVE_PILL_CLASS,
};

const CLIENT_TONE_CLASSES: Record<
  MemberStatusBadgeTone,
  Record<MemberStatusBadgeStatus, string>
> = {
  context: CLIENT_SOLID_CLASSES,
  product: {
    active: 'border border-emerald/20 bg-emerald/10 text-emerald',
    inactive: 'border border-gray-200 bg-gray-100 text-gray-600',
    termed: 'border border-gray-200 bg-gray-100 text-gray-600',
  },
  search: {
    active: 'bg-emerald-50 text-emerald-700',
    inactive: 'bg-gray-100 text-gray-600',
    termed: 'bg-gray-100 text-gray-600',
  },
  solid: CLIENT_SOLID_CLASSES,
};

const INTERNAL_TONE_CLASSES: Record<
  MemberStatusBadgeTone,
  Record<MemberStatusBadgeStatus, string>
> = {
  context: {...INTERNAL_SOLID_CLASSES, active: CBS_CURRENT_PILL_CLASS},
  product: {
    active: CBS_ACTIVE_PILL_CLASS,
    inactive: 'bg-slate-100 text-slate-500',
    termed: 'bg-slate-100 text-slate-500',
  },
  search: INTERNAL_SOLID_CLASSES,
  solid: INTERNAL_SOLID_CLASSES,
};

export function getMemberStatusBadgeLabel(status: MemberStatusBadgeStatus): string {
  if (status === 'active') return 'Active';
  if (status === 'inactive') return 'Inactive';
  return 'Termed';
}

export function resolveMemberStatusBadgeClass({
  className,
  portal,
  size = 'default',
  status,
  tone = 'solid',
}: {
  className?: string;
  portal: MemberStatusBadgePortal;
  size?: MemberStatusBadgeSize;
  status: MemberStatusBadgeStatus;
  tone?: MemberStatusBadgeTone;
}): string {
  const toneClasses = portal === 'internal' ? INTERNAL_TONE_CLASSES : CLIENT_TONE_CLASSES;
  return cn(
    'inline-flex items-center whitespace-nowrap rounded-full',
    SIZE_CLASSES[size],
    toneClasses[tone][status],
    className,
  );
}

export function MemberStatusBadge({
  className,
  label,
  size = 'default',
  status,
  tone = 'solid',
}: {
  className?: string;
  label?: ReactNode;
  size?: MemberStatusBadgeSize;
  status: MemberStatusBadgeStatus;
  tone?: MemberStatusBadgeTone;
}) {
  const portal = useIsInternal() ? 'internal' : 'client';
  return (
    <span className={resolveMemberStatusBadgeClass({className, portal, size, status, tone})}>
      {label ?? getMemberStatusBadgeLabel(status)}
    </span>
  );
}
